/**
 * Page Header Component
 * 
 * Reusable page header component with consistent styling and optional help icon.
 * Follows the established design pattern from Dashboard and Transactions pages.
 */

import React from 'react';
import { HelpCircle } from 'lucide-react';
import { HelpModal } from './HelpModal';

interface PageHeaderProps {
  title: string;
  description?: string;
  helpSlug?: string;
  children?: React.ReactNode; // For action buttons on the right
  className?: string;
  /**
   * Heading level for the title.
   * Defaults to 'h1' so top-level pages get a single page-level heading.
   * Nested pages (e.g. within Settings) should pass 'h2'.
   */
  headingLevel?: 'h1' | 'h2' | 'h3';
}

export function PageHeader({
  title,
  description,
  helpSlug,
  children,
  className = '',
  headingLevel = 'h1',
}: PageHeaderProps): React.ReactElement {
  const [showHelpModal, setShowHelpModal] = React.useState(false);

  const HeadingTag = headingLevel;

  return (
    <>
      <div className={`mb-8 ${className}`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <HeadingTag className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {title}
              </HeadingTag>
              {helpSlug && (
                <button
                  onClick={() => setShowHelpModal(true)}
                  className="group relative -ml-1 text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all duration-200 focus-ring flex-shrink-0 flex items-center justify-center min-w-[44px] min-h-[44px] p-2"
                  aria-label={`Help for ${title}`}
                  title={`Help for ${title}`}
                >
                  <HelpCircle className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" aria-hidden="true" strokeWidth={2} />
                  <span className="sr-only">View help for {title}</span>
                </button>
              )}
            </div>
            {description && (
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {description}
              </p>
            )}
          </div>

          {children && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              {children}
            </div>
          )}
        </div>
      </div>

      {helpSlug && (
        <HelpModal
          isOpen={showHelpModal}
          onClose={() => setShowHelpModal(false)}
          helpSlug={helpSlug}
        />
      )}
    </>
  );
}

